<template>
  <fieldset
    :class="_fs_classes"
    :aria-describedby="describedBy"
    :aria-labelledby="hint ? hintId : null"
  >
    <cat-label
      :label-id="id + '_lbl'"
      :fieldset="true"
      :label="label"
      :placeholder="offscreenLabel"
      :optional="!mandatory"
      :errors="errors"
      :readonly="readonly"
      :inline="help && !!help.url"
    ></cat-label>
    <cat-help-trigger
      v-if="help && help.url"
      :source-id="id"
      :url="help.url"
      :title="label"
    />
    <div v-if="help && help.url" class="clearfix"></div>
    <cat-hint :id="hintId" :text="hint" />
    <slot></slot>
    <cat-help
      v-if="help && help.text"
      :id="id + '_help'"
      :heading="help.title"
      :text="help.text"
      :aria-describedby="label && id ? id + '_lbl' : null"
    />
    <div
      v-if="offscreenDesc"
      :id="offscreenDescId"
      ref="offscreenDesc"
      style="display: none"
    >
      {{ offscreenDesc }}
    </div>
    <div class="radiogroup">
      <div
        v-for="(item, idx) in items"
        :key="idx"
        class="radio"
        :class="item.classes"
      >
        <input
          :id="id + '_' + idx"
          v-model="sel"
          class="input-radio"
          type="radio"
          tabindex="0"
          :name="id"
          :value="item.value"
          :readonly="readonly"
          :disabled="readonly"
          :aria-required="!!mandatory"
          :data-label="item.label"
          :aria-label="item.offscreenLabel"
          :aria-describedby="item.hint ? id + '_' + idx + '_hint' : null"
          @click="_onChange"
        />
        <label :for="id + '_' + idx">{{ item.label }}</label>
        <slot :name="'item-slot-after-label-' + idx" />
        <cat-hint
          v-if="item.hint"
          :id="id + '_' + idx + '_hint'"
          :text="item.hint"
        />
        <cat-help
          v-if="item.help"
          :id="id + '_' + idx + '_help'"
          :text="item.help"
          :heading="item.helpTitle"
          :aria-describedby="item.label && id ? id + '_' + idx : null"
        />
        <slot :name="'item-slot-end-' + idx" />
      </div>
    </div>
    <slot name="after-input"></slot>
    <cat-errors :errors="errors" />
  </fieldset>
</template>

<script lang="ts">
import { defineComponent, inject } from "vue";
import { defaultResolveTxt } from "@/components/field-element";
import CatErrors from "@/components/catErrors/catErrors.vue";
import CatHelp from "@/components/utilities/help.vue";
import CatLabel from "@/components/utilities/cat-label.vue";
import CatHint from "@/components/utilities/hint.vue";
import CatHelpTrigger from "@/components/catPageTemplate/catHelpTrigger";
export default defineComponent({
  setup() {
    // Inject the global value
    const resolveTxt = inject("resolveTxt", defaultResolveTxt);
    const displayParams = inject("displayParams", false);

    return {
      resolveTxt,
      displayParams
    };
  },
  name: "CatRadioButtons",
  components: {
    CatErrors,
    CatHelp,
    CatLabel,
    CatHelpTrigger,
    CatHint
  },
  props: {
    id: [Number, String],
    selected: {
      type: String,
      description: "Value of the selected item."
    },
    errors: [Array],
    items: {
      type: Array,
      description:
        "List of item objects with the following fields - value, label, hint, help."
    },
    row: [Boolean],
    mandatory: [Boolean, String],
    buttons: [Boolean],
    label: [String],
    offscreenLabel: [String],
    hint: [String],
    offscreenText: [String],
    readonly: [Boolean],
    vertical: [Boolean],
    help: [Object],
    segment: { type: Boolean, default: false },
    small: {
      type: Boolean,
      default: false,
      description: "Must be used in conjunction with Segment"
    },
    roundedsmall: {
      type: Boolean,
      default: false,
      description: "Must be used in conjunction with Segment"
    },
    rightAlign: {
      type: Boolean,
      default: false,
      description: "Aligns/floats the radiogroup to the right"
    },
    inlineLabel: {
      type: Boolean,
      default: false,
      description:
        "Align the label inline with the radio buttons. The radio buttons are then aligned to the right. Intended for a 'switch' (yes/no) style display used with segment and/or segment-small display."
    },
    compact: {
      type: Boolean,
      default: false,
      description:
        "Adds the 'compact' class to fieldset to remove margin for compact display."
    }
  },

  data: function () {
    return {
      sel: this.selected || this.selected === "" ? this.selected : null
    };
  },

  computed: {
    _fs_classes: function () {
      return {
        fieldset: true,
        "cat-radio-set": true,
        invalid: this.errors && this.errors.length > 0,
        required: this.mandatory,
        optional: !this.mandatory,
        "readonly-list": this.readonly,
        stacked: this.vertical,
        "radio-set-segment": this.segment,
        "segment-small": this.segment && this.small,
        "rounded-small": this.roundedsmall,
        "right-align": this.rightAlign,
        compact: this.compact,
        "inline-label": this.inlineLabel
      };
    },
    _findIndex: {
      get() {
        return this.items.findIndex((item) => item.value == this.sel);
      },
      set(v) {
        this.sel = this.items[v] ? this.items[v].value : this.sel;
        this._onChange();
      }
    },
    offscreenDesc: function () {
      return this.offscreenText && this.offscreenText !== ""
        ? this.offscreenText
        : null;
    },
    offscreenDescId: function () {
      return this.id + "-fs-offscreen-desc";
    },
    hintId: function () {
      return this.id + "-fs-hint";
    },
    describedBy: function () {
      var ids = [];
      if (this.hint) {
        ids.push(this.hintId);
      }
      if (this.offscreenDesc) {
        ids.push(this.offscreenDescId);
      }
      // Return space separated ids
      return ids.length > 0 ? ids.join(" ") : null;
    }
  },

  watch: {
    selected: function (newSel) {
      this.sel = newSel || newSel === "" ? newSel : null;
    }
  },

  methods: {
    _onChange: function (event) {
      //console.log("click", event, this.sel, event.target.value);
      // bound attribute isn't updated until after the click.  Need value from event target
      this.$emit("ui-change", {
        event: event,
        id: this.id ? this.id : null,
        value: event.target.value,
        el: this.$el
      });
    }
  }
});
</script>
